import { InputHTMLAttributes, TextareaHTMLAttributes, forwardRef } from "react";
import { cn } from "@/lib/utils";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
}

const BASE =
  "w-full rounded-lg border bg-cream-light px-4 py-3 text-[15px] text-navy-deep placeholder:text-taupe-light focus:outline-none focus:ring-2 focus:ring-amber/60 focus:border-amber/40 transition";

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, hint, error, className, id, ...rest }, ref) => {
    const fieldId = id || rest.name;
    return (
      <label className="block">
        {label ? (
          <span className="mb-1.5 block text-[13px] font-medium text-navy">{label}</span>
        ) : null}
        <input
          ref={ref}
          id={fieldId}
          className={cn(
            BASE,
            error ? "border-coral" : "border-cream-border",
            className,
          )}
          {...rest}
        />
        {hint && !error ? (
          <span className="mt-1 block text-[12px] text-taupe">{hint}</span>
        ) : null}
        {error ? (
          <span className="mt-1 block text-[12px] text-coral">{error}</span>
        ) : null}
      </label>
    );
  },
);
Input.displayName = "Input";

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, hint, className, rows = 4, ...rest }, ref) => (
    <label className="block">
      {label ? (
        <span className="mb-1.5 block text-[13px] font-medium text-navy">{label}</span>
      ) : null}
      <textarea
        ref={ref}
        rows={rows}
        className={cn(BASE, "resize-none", className)}
        {...rest}
      />
      {hint ? <span className="mt-1 block text-[12px] text-taupe">{hint}</span> : null}
    </label>
  ),
);
Textarea.displayName = "Textarea";
